import React, {Fragment, Suspense, useRef} from 'react';
import {Popup, Flex, Separator, Link, Breadcrumbs, Activities, Card, Tree, Goals, Button, Layout, Icon, Diary, ErrorBoundary} from 'components';
import {PlanSettings, PlanSorting, StartPoint} from '.';
import {redirect, last, useParams, useSuspenseQuery, wait, useSelector, api, createSlot, generate, openai} from 'helpers';
import {attach, removeImages} from 'store/activities';
import {update} from 'store/sorting';
import {goals} from 'store';
import chatCompletion from './test.json';
import {Activity, Goal} from 'types';

const Toolbar = createSlot();

export function Review() {
  const {id} = useParams<{id: string}>();
  const {goal, images} = useSelector(state => goals.details(state, {id, context: 'goals'}));
  return (
    <>
      <Layout.Header justified>
        <Breadcrumbs>
          <Link to="/goals/">Goals</Link>
          <b>Review Plan</b>
        </Breadcrumbs>
        <Toolbar.Placeholder />
      </Layout.Header>
      <Card background={last(images) || goal.category} status={goal.status} is-tall>
        <Card.Toolbar className={['no-mask', 'left']} />
        <Goals.Single.Card {...goal} ActionMenu={() => null} detailed />
      </Card>
      <div className="medium-spacing">
        <ErrorBoundary>
          <Suspense fallback={<Loading />}>
            <Creating />
          </Suspense>
        </ErrorBoundary>
      </div>
    </>
  );
}

const Creating = () => {
  const {id} = useParams<{id: string}>();
  const {goal} = useSelector(state => goals.details(state, {id, context: 'goals'}));

  const {data: milestones} = useSuspenseQuery({
    queryKey: ['openai', 'goal', 'plan', goal?.name],
    staleTime: Infinity,
    queryFn: async () => {
      const [choice] = await openai.chat.completions(`
        Act as a Goal Setting expert.
        You need to create a comprehensive, structured, and actionable milestone plan for the goal I provide. Ensure that each milestone follows the SMART criteria (Specific, Measurable, Achievable, Relevant, Time-bound) and builds progressively toward the goal.
        The response must be in JSON format, structured as follows:
        [
          {
            "name": "",
            "note": "",
            "activities": [
              { "name": "", "note": "" }
            ],
            "routines": [
              { "name": "", "note": "" }
            ]
          }
        ]
        Requirements for the Output:
          - Provide 5-7 key milestones that logically build toward the goal.
          - Each milestone should focus on one major phase of achieving the goal.
          - Each milestone must include:
          - 4-6 activities that cover different aspects (e.g., research, execution, feedback, scaling).
          - 1-3 routines that reinforce daily/weekly habits for long-term success.
          - Activities should have specific deliverables (e.g., "Create a business model draft" instead of "Plan the business model").
          - Routines should be long-term habits (e.g., "Review progress weekly" rather than one-time tasks).
          - Ensure that each milestone, activity, and routine has a short, clear name and a one-sentence description.
        Here is the goal: "${goal?.name}".
      `);
      return choice as Array<Goal & {activities: Activity[]}>;
    },
  });

  const accept = () => {
    const payload = {
      id,
      duplicateId: id,
      milestones: [] as Goal[],
      activities: [] as Activity[]
    };
    payload.milestones = milestones.map(({activities, routines, ...milestone}) => {
      const id = generate();
      payload.activities = [
        ...payload.activities,
        ...activities.map(activity => ({
          ...activity,
          goal_id: id,
          id: generate()
        }))
      ];
      return {
        ...milestone,
        id,
      }
    })
    goals.dispatch.duplicate(payload);
    redirect('../', true);
  };


  return (
    <Tree>
      { milestones.map(milestone =>
        <Tree.Item key={milestone.name}>
          <Card>
            <Card.Body>
              <Card.Text>{ milestone.name }</Card.Text>
              <Card.Note>{ milestone.note }</Card.Note>
            </Card.Body>
          </Card>
          { milestone.activities.length > 0 &&
            <Tree.Sub>
              { milestone.activities.map(activity =>
                <Card key={activity.name}>
                  <Card.Body>
                    <Card.Text>{ activity.name }</Card.Text>
                    <Card.Note>{ activity.note }</Card.Note>
                  </Card.Body>
                </Card>
              ) }
            </Tree.Sub>
          }
        </Tree.Item>
      ) }
      <Toolbar>
        <Flex>
          <Button
            className={['subtle', 'small']}
            to="../"
            children="Reject"
          />
          <Button
            className={['primary', 'small']}
            icon="check"
            children="Accept"
            onClick={accept}
          />
        </Flex>
      </Toolbar>
    </Tree>
  );
};

const Loading = () => (
  <>
    <Separator className="medium" heading="Creating an action plan" icon="spinner">
      <u />
    </Separator>
    <p>Please wait, this would take a minute ...</p>
    <Toolbar>
      <Button className={['small', 'invisible']}>_</Button>
    </Toolbar>
  </>
);
